import { useState, useEffect } from "react";
import {
  MapPin, Phone, Mail, Clock, ChevronRight, Star, Users, Trophy,
  Calendar, Shield, ArrowRight, Activity, TrendingUp, Target,
  Award, Zap, Menu, X, ExternalLink, BarChart3, Globe,
  Sparkles, Gamepad2, Wind, Timer
} from "lucide-react";
import { supabaseQuery } from "./supabase";

// 2026-08-04: every query on this page ran with no venue filter, because there
// has only ever been one centre behind it. Selling a copy of this site changes
// that — an unfiltered homepage would rank Townsville's players and list
// Townsville's competitions on somebody else's domain.
//
// One constant, read from the environment so a resold copy changes a value
// rather than the code. Defaults to Wildcatz so nothing moves here.
const VENUE_ID = process.env.REACT_APP_VENUE_ID || "4ce37f6b-08d1-4348-b752-e4619cee22d0";

// ── Wildcatz Brand Colors ──
const C = {
  teal: "#1a8a7d",
  tealDark: "#147267",
  tealLight: "#2ab5a5",
  red: "#c41e1e",
  redLight: "#e53535",
  black: "#0c1215",
  darkBg: "#0a0e10",
  card: "#111618",
  cardHover: "#1a2024",
  border: "#1e2a2e",
  borderLight: "#e2e8f0",
  // Light section colours
  white: "#ffffff",
  offWhite: "#f8fafb",
  lightCard: "#ffffff",
  lightText: "#1a1a1a",
  lightMuted: "#64748b",
  lightDim: "#94a3b8",
  // Dark section text
  text: "#e4e4e7",
  textMuted: "#94a3b8",
  textDim: "#64748b",
  purple: "#6C5CE7",
  orange: "#f97316",
};

// ── Data ──
// Sport images — keyed by sport slug
// To use your own photos: save them to wildcatz/public/ as /sports-{slug}.jpg
// and replace the Unsplash URLs below.
const SPORT_IMAGES = {
  cricket: "/Senior-Cricket.jpeg",
  soccer: "/Senior-Soccer.jpeg",       // 2026-05-27: dropped in resized 1600px Fire-vs-Road-Runners action shot
  netball: "/Senior-netball.JPG",      // 2026-05-26: refreshed photo
  volleyball: "/Beach-volleyball.JPEG",
  rebound: "/Beach-volleyball.JPEG",   // 2026-05-26: refreshed photo
  rounders: "/Senior-Rounders.jpeg",  // 2026-05-26: own action photo (frame from IMG_4845.MOV)
};

// Junior sport card image
const JUNIOR_IMAGE = "/sports-junior.jpeg";

// Display names for sports (override DB name if needed)
const SPORT_DISPLAY_NAMES = {
  cricket: "Indoor Cricket",
  soccer: "Indoor Soccer",
  netball: "Indoor Netball",
  volleyball: "Beach Volleyball",
  rebound: "Rebound Volleyball",
  rounders: "Rounders",
};

// Day abbreviation order for sorting
const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_SHORT = { Monday: "Mon", Tuesday: "Tue", Wednesday: "Wed", Thursday: "Thu", Friday: "Fri", Saturday: "Sat", Sunday: "Sun" };

// ── App Store Links ──
const APP_STORE_URL = "https://apps.apple.com/au/app/wildcatz-indoor-sports/id6761478923";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.wisasports.app"; // LIVE 2026-06-11 — Android app launched on Google Play

// 2026-10-15 (Jake): "we had that countdown originally firing the new
// attractuons etc which is on hold as we await council approvals etc. so turn
// that firing off and direct it to the webstore".
//
// The expansion teaser counted down to a reveal date the venue does not
// control - it waits on council. A countdown is a promise with a number on it,
// and the one thing worse than no date is a date that passes with nothing
// behind it. Parked until the approvals land.
//
// Nothing is deleted. Flip this back to false when council comes through and
// the teaser, its countdown and its nav entry all return exactly as they were;
// the reveal date still comes from the venue record, so there is no second
// place to update.
// 2026-10-22 (Jake): "lets get that back ... people already know we doing
// arcade ... they can see we doing arcade so we may aswell say youve seen the
// arcade but you have no idea whats coming next!"
//
// Back on. The teaser below now uses the arcade as the hook rather than
// pretending the whole thing is a secret: the one piece everyone has already
// seen is the proof that the rest is real.
const EXPANSION_ON_HOLD = false;

// Whether the site is allowed to reveal the activity line-up at all. False
// keeps the teaser showing "More to be announced soon" however the venue's
// reveal date is set. Flip to true when the five tiles should go public.
const EXPANSION_REVEAL_READY = false;

// 2026-10-20 (Jake): "maybe we stay vending only."
//
// The online store is off. This block used to count down to it opening on
// 1 November and then promise a Shop Now button, which is now a promise the
// club is not keeping — so the clock is pointed at something real instead.
//
// The Halloween Spooktacular lands the day before the store was due, and had
// 13 of 100 places gone with eleven days to run. The cards message stays and
// says what is true: they are in the machine and at the counter.
//
// Saturday 31 October, 1pm. Brisbane is UTC+10 all year, no daylight saving,
// so 1pm Brisbane is 03:00Z. Written as the instant rather than a local date
// so a visitor in another timezone still sees the right number of hours.
const HALLOWEEN_EVENT = new Date("2026-10-31T03:00:00Z");
const HALLOWEEN_RSVP_URL = "https://www.wisasports.com/wildcatz/events/halloween-spooktacular";

// ── "More Than Just Sport" — reveal date ──
// Before this date: show a big countdown timer in place of the activity tiles
// to build hype. After: the section automatically swaps to the 6-activity grid.
// Brisbane time so it counts down to midnight local. Change here when ready.
// 2026-06-22: pushed reveal from 30 Jun → 1 Aug, then 23 Jun: trimmed to 30 Jul.
// 2026-07-26 (Jake): pushed to end of August. Midnight on 1 Sep Brisbane means
// the countdown runs through the whole of August and hits zero as August ends.
// THE REVEAL DATE IS NOT DECIDED HERE ANY MORE.
//
// 2026-09-05: this was a literal, and moving it meant a redeploy of the
// website. It moved four times (30 Jun, 1 Aug, 30 Jul, 1 Sep, 15 Sep) and one
// of those times the database was left behind — for a month these tiles were
// hidden here while the booking flow was wide open.
//
// ── Where the backend is ─────────────────────────────────────────────────
//
// 2026-09-14. This was `process.env.REACT_APP_API_URL || ""` — a relative
// /api/… call — written twice in this file and wrong both times.
//
// Relative is right for the WISA frontend, where one Railway service serves
// the React build and the API on the same origin. It is wrong here: the
// wildcatz site is its own service with its own static server.js and no
// backend behind it. A relative /api call lands on the SPA catch-all and comes
// back as the page's own HTML, with a 200.
//
// It broke both callers, silently:
//
//   · the reveal date fetch, so the front page showed the 2099 fallback as
//     "26406 DAYS — Big reveal drops Thursday 1 January" to every visitor
//   · the franchise enquiry form, which posted, ignored the response and told
//     the person "sent". Nothing was sent. That section is hidden until
//     1 December, so no enquiry has been lost — it would have failed on its
//     first day.
//
// Verified: https://www.wisasports.com/api/venues/wildcatz/reveal returns JSON,
// and backend/src/index.js already lists www.wildcatzindoorsports.com.au in
// allowedOrigins. The env var still wins when set; what changed is that
// forgetting to set it no longer breaks the site in silence.
const API_BASE = process.env.REACT_APP_API_URL || "https://www.wisasports.com";

// The date now lives in one place, venues.membership_reveal_at, and this page
// asks for it: GET /api/venues/wildcatz/reveal.
//
// 2026-09-14: that fallback used to be a literal year 2099, on the reasoning
// that a site which reveals early cannot be un-revealed. Correct instinct,
// wrong mechanism — when the fetch silently failed, 2099 was not treated as
// "unknown", it was rendered as a real countdown, and the front page told
// every visitor the big reveal was 26,406 days away on Thursday 1 January.
//
// There is no fallback date now. Unknown is null, and null renders as "date
// to be announced" with the section still in its pre-reveal state. Safe in the
// same way, and legible when it happens.
//
// If Jake asks you to move the reveal, change the venue's date. Do not touch
// this file.

// The membership programme announces at the same instant ("yes we will announce
// at the same time" — Jake, 5 September 2026), so there is nothing else to keep
// in step: it reads the same column through the same endpoint.
function useRevealDate() {
  const [revealAt, setRevealAt] = useState(null);
  useEffect(() => {
    let alive = true;
    // 2026-09-14: REACT_APP_API_URL is not set on the deployed build, so this
    // asked wildcatzindoorsports.com.au for /api/venues/wildcatz/reveal. That
    // host is a static server with an SPA catch-all: it answered 200 with the
    // page's own HTML. r.ok was true, r.json() threw on "<!doctype html>", and
    // `.catch(() => {})` ate it — every time, forever.
    //
    // So the front page carried "26406 DAYS — Big reveal drops Thursday 1
    // January" to every visitor, which is the 2099 fallback rendered as though
    // it were news. The fallback did its job: nothing revealed early. What
    // failed is that nobody could tell it was failing.
    //
    // A swallowed error is the bug. This one now says so in the console and
    // hands back null, which the countdown reads as "no date" and renders
    // without a number rather than a wrong one.
    fetch(API_BASE + "/api/venues/wildcatz/reveal")
      .then((r) => {
        const type = r.headers.get("content-type") || "";
        if (!r.ok || !type.includes("application/json")) {
          throw new Error(`reveal: expected JSON, got ${r.status} ${type || "no content-type"}`);
        }
        return r.json();
      })
      .then((d) => {
        if (!alive || !d) return;
        // A venue with no date set is dark. Staying dark is the correct
        // reading of that, not an error.
        if (d.reveal_at) setRevealAt(new Date(d.reveal_at));
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("[wildcatz] could not read the reveal date, staying dark:", e.message);
        if (alive) setRevealAt(null);
      });
    return () => { alive = false; };
  }, []);
  return revealAt;
}

// Franchise enquiry section reveal — hidden until 1 December 2026 Brisbane time.
// 2026-05-27: Jake hiding the franchise CTA until we're actually ready to take
// expansion enquiries. Same auto-reveal pattern, but this one is genuinely
// local: it is not tied to the venue's public announcement.
const FRANCHISE_REVEAL_DATE = new Date("2026-12-01T00:00:00+10:00");

// Countdown timer for the "More Than Just Sport" reveal. Big bold tiles
// for days / hours / mins / secs, ticks every second, swaps to the activity
// grid automatically once the target date passes. Pure CSS animation, no
// external deps. Uses Black Han Sans (matching brand) for the numbers.
function CountdownTimer({ target, note }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  // 2026-09-14: no date, or one so far out it is obviously the fallback rather
  // than a plan, means we do not know when the reveal is. Say that. A number
  // nobody believes — 26406 DAYS sat on the front page — is worse than no
  // number, because it tells a visitor the site is broken.
  const unknown = !target || !(target instanceof Date) || Number.isNaN(target.getTime())
    || target.getTime() - now.getTime() > 365 * 24 * 60 * 60 * 1000;
  if (unknown) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', fontSize: '0.9rem', color: 'rgba(255,255,255,0.55)' }}>
        ⏰ Date to be announced. Download the app and we will tell you first.
      </div>
    );
  }
  const ms = Math.max(0, target.getTime() - now.getTime());
  const days  = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
  const mins  = Math.floor((ms / (1000 * 60)) % 60);
  const secs  = Math.floor((ms / 1000) % 60);
  const pad = (n) => String(n).padStart(2, '0');
  const tiles = [
    { value: days,  label: 'Days'    },
    { value: pad(hours), label: 'Hours'   },
    { value: pad(mins),  label: 'Minutes' },
    { value: pad(secs),  label: 'Seconds' },
  ];
  const revealLabel = target.toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long',
    timeZone: 'Australia/Brisbane',
  });
  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <style>{`
        @keyframes wcCountPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(196,30,30,0.4); } 50% { box-shadow: 0 0 0 8px rgba(196,30,30,0); } }
        @keyframes wcCountGlow  { 0%,100% { text-shadow: 0 0 20px rgba(196,30,30,0.3); } 50% { text-shadow: 0 0 32px rgba(196,30,30,0.6); } }
      `}</style>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {tiles.map((t, i) => (
          <div key={i} style={{
            background: 'linear-gradient(135deg, #111618 0%, #0a0e10 100%)',
            border: '1px solid rgba(196,30,30,0.25)',
            borderRadius: 14,
            padding: '28px 8px 18px',
            textAlign: 'center',
            animation: i === 3 ? 'wcCountPulse 2s ease-in-out infinite' : 'none',
          }}>
            <div style={{
              fontFamily: "'Black Han Sans', sans-serif",
              fontSize: 'clamp(2.6rem, 8vw, 5rem)',
              lineHeight: 1,
              color: '#fff',
              letterSpacing: '-2px',
              animation: i === 3 ? 'wcCountGlow 2s ease-in-out infinite' : 'none',
            }}>{t.value}</div>
            <div style={{
              fontSize: '0.7rem', fontWeight: 700, letterSpacing: 3,
              color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase',
              marginTop: 10,
            }}>{t.label}</div>
          </div>
        ))}
      </div>
      <div style={{ textAlign: 'center', fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)' }}>
        {note
          ? note(revealLabel)
          : <>⏰ Big reveal drops <strong style={{ color: '#fff' }}>{revealLabel}</strong>. Be the first to know — get the app.</>}
      </div>
    </div>
  );
}

// Google Play badge — always rendered, but shown dimmed with a "Coming soon"
// chip until GOOGLE_PLAY_URL is set above. When the Play Store launch lands
// just flip the URL constant and the badge auto-activates on every spot it
// appears (hero, CTA section, app section, footer).
// 2026-06-14: visual-size fix. Google's official PNG badge has ~22% internal
// padding around the artwork while Apple's SVG is tight-cropped — at the
// same `height` the App Store badge looks noticeably bigger. Scale the
// Google img up by 1.28x and absorb the overflow with negative margins
// inside a clipped wrapper so the visible artwork matches Apple optically
// and the row height stays the same.
function GooglePlayBadge({ height }) {
  const live = !!GOOGLE_PLAY_URL;
  const imgH = Math.round(height * 1.28);
  const overflow = Math.round((imgH - height) / 2);
  const img = (
    <img
      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
      alt={live ? "Get it on Google Play" : "Get it on Google Play — coming soon"}
      style={{
        height: imgH,
        display: 'block',
        marginTop: -overflow,
        marginBottom: -overflow,
        filter: live ? 'none' : 'grayscale(70%) brightness(0.7)',
      }}
    />
  );
  const wrap = (children) => (
    <span style={{ display: 'inline-block', height, overflow: 'hidden', verticalAlign: 'middle', lineHeight: 0 }}>
      {children}
    </span>
  );
  if (live) {
    return <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer">{wrap(img)}</a>;
  }
  return (
    <div style={{ position: 'relative', display: 'inline-block', cursor: 'not-allowed' }}
         title="Coming soon to Google Play">
      {wrap(img)}
      <div style={{
        position: 'absolute', top: -6, right: -6,
        background: '#1a8a7d', color: '#fff', fontSize: '0.55rem',
        fontWeight: 800, letterSpacing: 1, padding: '2px 6px',
        borderRadius: 4, textTransform: 'uppercase',
        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
      }}>Coming Soon</div>
    </div>
  );
}

const STATS = [
  { value: "450+", label: "Weekly Teams", icon: Users },
  { value: "2,500+", label: "Active Athletes", icon: Activity },
  { value: "20+", label: "Years Running", icon: Clock },
  { value: "350K+", label: "Games Played", icon: Trophy },
];

const TESTIMONIALS = [
  { name: "Alex", role: "Court Hire", text: "Wildcatz offers a great venue to train and socialise afterwards! Affordable indoor option!" },
  { name: "Josh", role: "Cricket Player", text: "Cricket at Wildcatz is a great social activity where you get to play with mates and catch up each week." },
  { name: "Chris", role: "Volleyball Player", text: "I play rebound at Wildcatz 4 days a week! Great social fun and value for money! Highly recommend." },
  { name: "Taylor", role: "Soccer Player", text: "The football facilities at Wildcatz are second to none, providing the perfect environment to hone your skills." },
];

// STEPS removed — replaced by single Book Now CTA

const TOP_PLAYERS = [
  { rank: 1, name: "Jake R.", sport: "Soccer", rating: 2847, trend: "+12", badge: "🔥" },
  { rank: 2, name: "Chris T.", sport: "Volleyball", rating: 2691, trend: "+8", badge: "⚡" },
  { rank: 3, name: "Sarah M.", sport: "Netball", rating: 2583, trend: "+15", badge: "🌟" },
  { rank: 4, name: "Josh W.", sport: "Cricket", rating: 2510, trend: "+5", badge: "" },
  { rank: 5, name: "Taylor K.", sport: "Soccer", rating: 2488, trend: "+3", badge: "" },
];

const LIVE_MATCHES = [
  { sport: "Soccer", home: "Thunder FC", away: "Lightning XI", score: "3 – 2", time: "FT", court: "Court 1" },
  { sport: "Netball", home: "Storm", away: "Blaze", score: "28 – 25", time: "Q4", court: "Court 2" },
  { sport: "Cricket", home: "Wildcatz XI", away: "Cobras", score: "145/6", time: "Inn 2", court: "Court 3" },
];

const COMING_ACTIVITIES = [
  { name: "Tenpin Bowling", emoji: "🎳", color: C.orange, desc: "Leagues, glow bowling & party packages", icon: Target },
  { name: "Golf Simulators", emoji: "⛳", color: C.teal, desc: "100+ world-famous courses indoors", icon: Wind },
  { name: "AR Sports", emoji: "🎯", color: "#06b6d4", desc: "Augmented reality meets real sport", icon: Sparkles },
  { name: "Virtual Reality", emoji: "🥽", color: C.purple, desc: "Immersive VR gaming experiences", icon: Gamepad2 },
  { name: "Arcade", emoji: "🕹️", color: "#f59e0b", desc: "Classic & modern arcade games", icon: Gamepad2 },
];

// ══════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════
/**
 * Build sports list from DB data.
 * Groups competitions by sport, extracts unique days, determines age range.
 */
function buildSportsFromDB(sports, competitions) {
  // Group competitions by sport_id
  const compsBySport = {};
  for (const c of competitions) {
    if (!compsBySport[c.sport_id]) compsBySport[c.sport_id] = [];
    compsBySport[c.sport_id].push(c);
  }

  return sports
    .filter(s => compsBySport[s.id]) // only sports with active comps
    .map(s => {
      const comps = compsBySport[s.id];
      // Days + age labels should reflect HEADLINE competitions only so the
      // copy matches what's actually running publicly. (A junior grade that's
      // tagged is_grade=true shouldn't make the sport read 'All Ages' if the
      // headline junior comp doesn't exist.)
      const realComps = comps.filter(c => !c.is_grade && !c.is_scratch && !c.eoi_only && c.grade !== 'Scratch');
      const days = [...new Set(realComps.map(c => c.day_of_week).filter(Boolean))];
      days.sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
      const dayStr = days.map(d => DAY_SHORT[d] || d).join(", ");

      const hasJunior = realComps.some(c => c.is_junior);
      const hasSenior = realComps.some(c => !c.is_junior);
      const ages = hasJunior && hasSenior ? "All Ages" : hasJunior ? "Juniors" : "Seniors";

      // 2026-05-27: only count REAL top-level competitions for the
      // marketing description. Excludes:
      //  • is_grade=true  — Div 1/2/3 sub-divisions of a parent comp
      //  • is_scratch=true — placeholder scratch comps for cross-league play
      //  • eoi_only=true  — expression-of-interest placeholders, not yet running
      //  • grade='Scratch' — legacy scratch label
      const isHeadline = c => !c.is_grade && !c.is_scratch && !c.eoi_only && c.grade !== "Scratch";
      const totalComps  = comps.filter(isHeadline).length;
      const juniorComps = comps.filter(c => isHeadline(c) && c.is_junior).length;
      const seniorComps = comps.filter(c => isHeadline(c) && !c.is_junior).length;

      // Build description
      const parts = [];
      if (seniorComps > 0) parts.push(`${seniorComps} senior competition${seniorComps > 1 ? "s" : ""}`);
      if (juniorComps > 0) parts.push(`${juniorComps} junior program${juniorComps > 1 ? "s" : ""}`);
      const desc = parts.length
        ? `${parts.join(" and ")} running across ${days.length} day${days.length > 1 ? "s" : ""} a week. All skill levels welcome.`
        : `Competitions running ${dayStr}. All skill levels welcome.`;

      return {
        name: SPORT_DISPLAY_NAMES[s.slug] || s.name,
        emoji: s.icon,
        color: s.color,
        ages,
        desc: desc.charAt(0).toUpperCase() + desc.slice(1),
        days: dayStr || "TBA",
        image: SPORT_IMAGES[s.slug] || "https://images.unsplash.com/photo-1461896836934-bd45ba8a0d36?w=600&h=400&fit=crop",
        slug: s.slug,
        totalComps,
      };
    })
    .sort((a, b) => b.totalComps - a.totalComps); // most active sports first
}

export default function Wildcatz() {
  // The reveal instant, fetched from the venue rather than compiled in.
  const comingSoonRevealDate = useRevealDate();
  // Clears the Halloween section and its nav entry the moment the doors open,
  // so nobody has to edit the site on a Saturday afternoon. One timer that
  // fires once, rather than a per-second poll re-rendering the whole page.
  const [halloweenPassed, setHalloweenPassed] = useState(() => Date.now() >= HALLOWEEN_EVENT.getTime());
  useEffect(() => {
    if (halloweenPassed) return undefined;
    const ms = HALLOWEEN_EVENT.getTime() - Date.now();
    // setTimeout overflows past ~24.8 days and would fire immediately.
    const id = setTimeout(() => setHalloweenPassed(true), Math.min(ms, 2147483647));
    return () => clearTimeout(id);
  }, [halloweenPassed]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [sports, setSports] = useState([]);
  const [juniorDays, setJuniorDays] = useState("");
  // 2026-05-27: Junior Sport modal — opens when the Junior Sport tile is
  // clicked so parents see the available junior programs (with day, age, photo,
  // contact) instead of being dumped onto the WISA login page.
  const [juniorModalOpen, setJuniorModalOpen] = useState(false);
  // 2026-05-27: FAQ accordion — null = all closed, otherwise the open index
  const [openFaq, setOpenFaq] = useState(null);
  const [franchiseForm, setFranchiseForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [franchiseStatus, setFranchiseStatus] = useState(null); // null | "sending" | "sent" | "error"
  // Live top-5 leaderboard for the All4Indoor widget. Aggregates all
  // player_stats rows per user across every sport — sum goals, wickets, runs.
  // Falls back to the static TOP_PLAYERS placeholder if the query fails or
  // returns nothing (e.g. anon RLS lockdown). 2026-05-27.
  const [topPlayers, setTopPlayers] = useState(TOP_PLAYERS);
  // Live "Live at Wildcatz" hero widget. Pulls today's matches (Brisbane day
  // window). Priority order: in_progress → recently completed → upcoming.
  // Falls back to the hardcoded LIVE_MATCHES placeholder until the live data
  // lands so the hero never looks empty. 2026-05-27.
  const [liveMatches, setLiveMatches] = useState(LIVE_MATCHES);

  useEffect(() => {
    const t = setInterval(() => setActiveTestimonial(p => (p + 1) % TESTIMONIALS.length), 5000);
    return () => clearInterval(t);
  }, []);

  // Fetch sports & competitions from Supabase
  useEffect(() => {
    let cancelled = false;
    async function fetchSports() {
      try {
        const [sportsData, compsData] = await Promise.all([
          supabaseQuery("sports", "select=id,name,slug,icon,color,min_players,is_core"),
          supabaseQuery(
            "competitions",
            // 2026-05-27: pull is_grade + is_scratch + eoi_only so we can
            // exclude sub-division rows (Div 1/2/3 of a parent comp) from
            // the marketing-site headcount. Previously every grade row was
            // being counted as its own competition — so "Monday Volleyball"
            // appeared as 3 competitions when it's actually 1.
            "select=id,sport_id,name,day_of_week,is_junior,status,grade,archived,is_grade,is_scratch,eoi_only,parent_competition_id" +
            `&venue_id=eq.${VENUE_ID}` +
            "&status=eq.active&or=(archived.is.null,archived.eq.false)"
          ),
        ]);
        if (!cancelled) {
          setSports(buildSportsFromDB(sportsData, compsData));
          // Compute junior days
          const jDays = [...new Set(
            compsData.filter(c => c.is_junior && c.day_of_week).map(c => c.day_of_week)
          )].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
          setJuniorDays(jDays.map(d => DAY_SHORT[d] || d).join(", "));
        }
      } catch (err) {
        console.error("[Wildcatz] Failed to fetch sports:", err);
      }
    }
    fetchSports();
    return () => { cancelled = true; };
  }, []);

  // ─── LIVE TOP 5 PLAYERS (all sports, career impact) ──────────────────────
  // Pulls every row of player_stats + every non-parent user via the anon REST
  // API, aggregates by user_id, and ranks by an impact score:
  //   impact = goals + (wickets * 2) + (runs / 15)
  // Primary sport = whichever sport row has the most games_played. We show
  // games_played as the "trend" so the right-hand stat still reads naturally.
  // If anything fails or returns no usable rows, we silently keep the
  // hardcoded TOP_PLAYERS placeholder — visitors never see a broken widget.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 2026-08-04: this used to pull EVERY player_stats row and EVERY
        // non-parent user (limit 20000) and aggregate in the browser — so
        // every anonymous visitor downloaded the full member list, 2,589
        // names, to render five of them. 691 KB of JSON for a five-row widget,
        // and no venue filter.
        //
        // top_players() does the same arithmetic in the database and returns
        // five rows: 0.7 KB, no roster, scoped to this venue. The weighting
        // and the "must have played a tracked game" filter moved with it, so
        // the order on screen is unchanged.
        const rows = await supabaseQuery(
          "rpc/top_players", `p_venue=${VENUE_ID}&p_limit=5`);
        if (cancelled) return;
        const ranked = (rows || []).map(r => {
          const parts = String(r.display_name || "").trim().split(" ");
          return {
            userId: r.user_id,
            // "First L." for parity with the hardcoded placeholder.
            name: parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0],
            sport: String(r.primary_sport || "Multi-sport").replace(/^Indoor\s+/i, ""),
            rating: Math.round(Number(r.impact) || 0),
            games: Number(r.games_played) || 0,
          };
        });
        if (ranked.length >= 3 && !cancelled) {
          setTopPlayers(ranked.map((p, i) => ({
            rank: i + 1,
            name: p.name,
            sport: p.sport,
            rating: p.rating,
            trend: `${p.games} games`,
            badge: i === 0 ? "🔥" : i === 1 ? "⚡" : i === 2 ? "🌟" : "",
            isLive: true,
          })));
        }
      } catch (e) {
        // Silent — placeholder stays in place
        console.warn("[Wildcatz] Leaderboard fetch failed", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── LIVE AT WILDCATZ (hero widget) ──────────────────────────────────────
  // Pulls today's matches (Brisbane day window — UTC+10) plus anything
  // currently in_progress regardless of date. Priority order shown:
  //   1. status=in_progress (LIVE pill)
  //   2. status=completed   (FT pill, most recent first)
  //   3. status=scheduled   (kick-off time pill, soonest first)
  // Up to 3 cards. Polls every 60s so scores update through the night.
  useEffect(() => {
    let cancelled = false;
    const fmtSport = (name) => (name || "").replace(/^Indoor\s+/i, "");
    const fmtScore = (h, a) => {
      if (h == null && a == null) return "vs";
      return `${h ?? 0} – ${a ?? 0}`;
    };
    const fmtTime = (iso) => {
      try {
        return new Date(iso).toLocaleTimeString("en-AU", {
          hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Australia/Brisbane"
        }).replace(/\s/g, "");
      } catch { return ""; }
    };
    const todayBrisbaneRange = () => {
      const tz = 10 * 60 * 60 * 1000; // UTC+10
      const nowUtc = Date.now();
      const brisbane = new Date(nowUtc + tz);
      brisbane.setUTCHours(0, 0, 0, 0);
      const start = new Date(brisbane.getTime() - tz);
      const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
      return { startIso: start.toISOString(), endIso: end.toISOString() };
    };
    async function fetchLive() {
      try {
        const { startIso, endIso } = todayBrisbaneRange();
        const select =
          "select=id,scheduled_at,status,home_score,away_score," +
          "home_team:teams!home_team_id(name)," +
          "away_team:teams!away_team_id(name)," +
          "competition:competitions(name,sport:sports(name,slug))," +
          "court:courts(name)";
        // Two queries: today's matches + anything currently in_progress
        const [todays, live] = await Promise.all([
          supabaseQuery("matches", `${select}&venue_id=eq.${VENUE_ID}&scheduled_at=gte.${startIso}&scheduled_at=lt.${endIso}&order=scheduled_at.asc&limit=30`),
          supabaseQuery("matches", `${select}&venue_id=eq.${VENUE_ID}&status=eq.in_progress&order=scheduled_at.desc&limit=10`),
        ]);
        if (cancelled) return;
        // De-dup by id, prefer the in_progress copy
        const byId = new Map();
        for (const m of (todays || [])) byId.set(m.id, m);
        for (const m of (live || [])) byId.set(m.id, m);
        const all = Array.from(byId.values());

        const inProg  = all.filter(m => m.status === "in_progress");
        const done    = all.filter(m => m.status === "completed").sort((a, b) =>
          new Date(b.scheduled_at) - new Date(a.scheduled_at));
        const upcoming = all.filter(m => m.status === "scheduled" && new Date(m.scheduled_at) >= new Date())
          .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

        const chosen = [...inProg, ...done, ...upcoming].slice(0, 3);
        if (chosen.length === 0) return; // keep placeholder

        const mapped = chosen.map(m => ({
          sport: fmtSport(m.competition?.sport?.name) || "Match",
          home:  m.home_team?.name || "Home",
          away:  m.away_team?.name || "Away",
          score: m.status === "scheduled" ? "vs" : fmtScore(m.home_score, m.away_score),
          time:  m.status === "in_progress" ? "LIVE"
               : m.status === "completed"   ? "FT"
               : fmtTime(m.scheduled_at),
          court: m.court?.name || "",
          isLive: m.status === "in_progress",
        }));
        if (!cancelled) setLiveMatches(mapped);
      } catch (e) {
        console.warn("[Wildcatz] Live matches fetch failed", e?.message);
      }
    }
    fetchLive();
    const interval = setInterval(fetchLive, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const navLinks = [
    { label: "Sports", href: "#sports" },
    { label: "Leaderboard", href: "#leaderboard" },
    // The cards section is gone (2026-10-22). The Halloween link follows the
    // same rule as Coming Soon below: it exists only while its section does.
    ...(halloweenPassed ? [] : [{ label: "Halloween", href: "#halloween" }]),
    // While the expansion is on hold there is nothing behind "Coming Soon",
    // and a nav item that scrolls to a section that is not rendered is a
    // broken link the visitor blames themselves for.
    ...(EXPANSION_ON_HOLD ? [] : [{ label: "Coming Soon", href: "#coming-soon-preview" }]),
    { label: "Contact", href: "#contact" },
  ];

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", background: C.white, color: C.lightText, minHeight: "100vh" }}>

      {/* ── NAVBAR (dark) ── */}
      <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(12,18,21,0.95)", backdropFilter: "blur(20px)", borderBottom: `1px solid ${C.border}` }}>
        <div className="wc-container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 92 }}>
          <a href="#" style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {/* 2026-05-27: Wildcatz 50px, Ninja Parc 75px (1.5×) so the
                Ninja Parc wordmark dominates the franchise pairing. */}
            <img src="/wildcatz-logo.png" alt="Wildcatz Indoor Sports" style={{ height: 50 }} className="wc-nav-logo" />
            <div style={{ width: 1, height: 56, background: "rgba(255,255,255,0.15)" }} />
            <a href="https://book.ninjaparc.com.au/location/townsville" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center" }}>
              <img src="/ninjaparc-logo.svg" alt="Ninja Parc" style={{ height: 75 }} className="wc-nav-logo-np" />
            </a>
          </a>

          {/* Desktop nav */}
          <div className="wc-desktop-nav" style={{ display: "flex", alignItems: "center", gap: 32 }}>
            {navLinks.map(l => (
              <a key={l.label} href={l.href} {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} style={{ fontSize: "0.85rem", fontWeight: 500, color: C.textMuted, transition: "color 0.2s" }}
                onMouseEnter={e => e.target.style.color = C.white} onMouseLeave={e => e.target.style.color = C.textMuted}>
                {l.label}
              </a>
            ))}
            <a href="https://www.wisasports.com/wildcatz/book" target="_blank" rel="noopener noreferrer"
              style={{ background: C.white, color: C.black, padding: "10px 24px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 700, transition: "all 0.2s", boxShadow: "0 0 12px rgba(255,255,255,0.15)" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#e8edef"; e.currentTarget.style.boxShadow = "0 0 20px rgba(255,255,255,0.25)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.boxShadow = "0 0 12px rgba(255,255,255,0.15)"; }}>
              Book Now
            </a>
            <a href="https://www.wisasports.com/wildcatz/register" target="_blank" rel="noopener noreferrer"
              style={{ background: C.teal, color: C.white, padding: "10px 24px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 700, transition: "background 0.2s" }}>
              Register Now
            </a>
          </div>

          {/* Mobile burger */}
          <button className="wc-mobile-burger" onClick={() => setMenuOpen(!menuOpen)}
            style={{ display: "none", background: "none", border: "none", color: C.white, cursor: "pointer", padding: 4 }}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="wc-mobile-menu" style={{ background: C.card, borderTop: `1px solid ${C.border}`, padding: "16px 20px" }}>
            {navLinks.map(l => (
              <a key={l.label} href={l.href} {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} onClick={() => setMenuOpen(false)}
                style={{ display: "block", padding: "12px 0", fontWeight: 600, color: C.text, borderBottom: `1px solid ${C.border}`, fontSize: "0.95rem" }}>
                {l.label}
              </a>
            ))}
            <a href="https://www.wisasports.com/wildcatz/book" target="_blank" rel="noopener noreferrer"
              style={{ display: "block", textAlign: "center", background: C.white, color: C.black, padding: "12px", borderRadius: 10, fontWeight: 700, marginTop: 12, boxShadow: "0 0 12px rgba(255,255,255,0.15)" }}>
              Book Now
            </a>
            <a href="https://www.wisasports.com/wildcatz/register" target="_blank" rel="noopener noreferrer"
              style={{ display: "block", textAlign: "center", background: C.teal, color: C.white, padding: "12px", borderRadius: 10, fontWeight: 700, marginTop: 8 }}>
              Register Now
            </a>
          </div>
        )}
      </nav>

      {/* ── HERO (dark) ── */}
      <section style={{ position: "relative", overflow: "hidden", padding: "75px 0 80px", background: `linear-gradient(180deg, ${C.black} 0%, #0f1a1d 100%)` }}>
        {/* Background accents */}
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 20% 50%, rgba(26,138,125,0.12) 0%, transparent 60%)" }} />
        <div style={{ position: "absolute", top: -200, right: -200, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(196,30,30,0.06), transparent 70%)" }} />
        <div style={{ position: "absolute", left: "5%", top: "20%", width: 2, height: 120, background: `linear-gradient(to bottom, transparent, ${C.teal}55, transparent)` }} />
        {/* 2026-05-27: Mascot watermark — big translucent Wildcatz cat tucked
            into the bottom-right of the hero. Adds brand presence without
            competing with the headline or the live-scores widget. Hidden on
            mobile via the wc-hero-mascot class so it doesn't overlap text. */}
        <img src="/wildcatz-logo.png" alt="" aria-hidden="true" className="wc-hero-mascot"
          style={{ position: "absolute", right: -60, bottom: -40, width: 480, opacity: 0.08,
                   pointerEvents: "none", filter: "saturate(0.4) brightness(1.1)",
                   transform: "rotate(-8deg)", zIndex: 0 }} />

        <div className="wc-container" style={{ position: "relative" }}>
          <div style={{ maxWidth: 720 }}>
            {/* Badge */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(26,138,125,0.12)", border: "1px solid rgba(26,138,125,0.25)", borderRadius: 20, padding: "6px 16px", marginBottom: 28 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.teal }} />
              <span style={{ fontSize: "0.78rem", fontWeight: 600, color: C.teal }}>Townsville's #1 Indoor Sports Venue</span>
            </div>

            <h1 className="wc-hero-title" style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 900, lineHeight: 1.05, letterSpacing: "-1.5px", color: C.white, marginBottom: 24 }}>
              Where Townsville<br />
              <span style={{ color: C.teal }}>Plays Indoor.</span>
            </h1>
            <p style={{ fontSize: "1.15rem", lineHeight: 1.7, color: C.textMuted, maxWidth: 520, marginBottom: 40 }}>
              Cricket, Soccer, Netball, Rounders & Rebound Beach Volleyball — social leagues every week for all ages. Junior sport, court hire, birthday parties and more. 20+ years of bringing Townsville together through sport.
            </p>

            <div className="wc-hero-buttons" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href="https://www.wisasports.com/wildcatz/register" target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: C.teal, color: C.white, padding: "14px 32px", borderRadius: 12, fontWeight: 700, fontSize: "0.95rem", transition: "transform 0.2s" }}>
                Register Now <ArrowRight size={18} />
              </a>
              <a href="#sports"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: `1px solid ${C.border}`, color: C.white, padding: "14px 32px", borderRadius: 12, fontWeight: 600, fontSize: "0.95rem" }}>
                View Sports <ChevronRight size={18} />
              </a>
            </div>
            {/* App Store badges */}
            <div style={{ display: "flex", gap: 12, marginTop: 24, alignItems: "center", flexWrap: "wrap" }}>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" style={{ height: 44 }} />
              </a>
              <GooglePlayBadge height={44} />
              <span style={{ fontSize: "0.78rem", color: C.textDim }}>Stay up to date with all the news</span>
            </div>
          </div>

          {/* Live matches widget */}
          <div className="wc-hero-live" style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", width: 320 }}>
            <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.red, animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: "0.78rem", fontWeight: 700, color: C.white }}>Live at Wildcatz</span>
              </div>
              {liveMatches.map((m, i) => (
                <div key={i} style={{ padding: "12px 18px", borderBottom: i < liveMatches.length - 1 ? `1px solid ${C.border}` : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: "0.7rem", color: C.textMuted }}>{m.sport}{m.court ? ` · ${m.court}` : ""}</span>
                    <span style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: m.isLive ? C.white : C.teal,
                      background: m.isLive ? C.red : "rgba(26,138,125,0.12)",
                      padding: "2px 8px",
                      borderRadius: 4,
                      animation: m.isLive ? "pulse 2s infinite" : "none",
                    }}>{m.time}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600, color: C.white, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.home}</span>
                    <span style={{ fontSize: "0.85rem", fontWeight: 800, color: C.teal, padding: "0 8px", flexShrink: 0 }}>{m.score}</span>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600, color: C.white, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "right" }}>{m.away}</span>
                  </div>
                </div>
              ))}
              <a href="https://www.wisasports.com/wildcatz/fixtures" target="_blank" rel="noopener noreferrer"
                style={{ display: "block", padding: "10px 18px", textAlign: "center", fontSize: "0.75rem", fontWeight: 600, color: C.teal, borderTop: `1px solid ${C.border}` }}>
                View All Fixtures →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS STRIP (teal bg) ── */}
      <section style={{ background: C.teal }}>
        <div className="wc-container wc-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)" }}>
          {STATS.map((s, i) => (
            <div key={i} style={{ padding: "32px 0", textAlign: "center", borderRight: i < 3 ? "1px solid rgba(255,255,255,0.2)" : "none" }}>
              <s.icon size={20} color="rgba(255,255,255,0.8)" style={{ marginBottom: 8 }} />
              <div style={{ fontSize: "1.8rem", fontWeight: 900, color: C.white, letterSpacing: "-1px" }}>{s.value}</div>
              <div style={{ fontSize: "0.78rem", fontWeight: 500, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SPORTS (light bg) ── */}
      <section id="sports" style={{ padding: "80px 0", background: C.offWhite }}>
        <div className="wc-container">
          <div style={{ marginBottom: 48, textAlign: "center" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.teal, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>Our Sports</div>
            <h2 style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", fontWeight: 900, color: C.lightText, letterSpacing: "-0.5px" }}>
              {sports.length ? `${sports.length + 1} Programs. Seven Days a Week.` : "Loading Sports..."}
            </h2>
          </div>
          <div className="wc-sports-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {sports.length === 0 && [0,1,2,3,4,5].map(i => (
              <div key={i} style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.borderLight}`, overflow: "hidden", opacity: 0.5 }}>
                <div style={{ height: 200, background: `linear-gradient(135deg, ${C.offWhite}, #e2e8f0)` }} />
                <div style={{ padding: 20 }}>
                  <div style={{ height: 14, background: C.offWhite, borderRadius: 4, marginBottom: 10, width: "80%" }} />
                  <div style={{ height: 12, background: C.offWhite, borderRadius: 4, width: "60%" }} />
                </div>
              </div>
            ))}
            {sports.map((sport, i) => (
              <div key={sport.slug || i} style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.borderLight}`, overflow: "hidden", transition: "box-shadow 0.3s, transform 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 30px rgba(26,138,125,0.12)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                <div style={{ position: "relative", height: 200, overflow: "hidden" }}>
                  <img src={sport.image} alt={sport.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)" }} />
                  <div style={{ position: "absolute", bottom: 16, left: 20, right: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: "1.5rem" }}>{sport.emoji}</span>
                      <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: C.white }}>{sport.name}</h3>
                    </div>
                  </div>
                  <div style={{ position: "absolute", top: 12, right: 12, background: "rgba(26,138,125,0.9)", padding: "4px 12px", borderRadius: 8, fontSize: "0.72rem", fontWeight: 600, color: C.white }}>{sport.ages}</div>
                </div>
                <div style={{ padding: "20px" }}>
                  <p style={{ fontSize: "0.88rem", color: C.lightMuted, lineHeight: 1.6, marginBottom: 14 }}>{sport.desc}</p>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: C.lightDim }}>
                      <Calendar size={14} /> {sport.days}
                    </div>
                    <a href="https://www.wisasports.com/wildcatz/register" target="_blank" rel="noopener noreferrer"
                      style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.82rem", fontWeight: 600, color: C.teal }}>
                      Register <ArrowRight size={14} />
                    </a>
                  </div>
                </div>
              </div>
            ))}

            {/* Junior Sport highlight card — styled to match the sport tiles */}
            {sports.length > 0 && (() => {
              const juniorSports = sports.filter(s => s.ages === "All Ages" || s.ages === "Juniors");
              const juniorNames = juniorSports.map(s => s.name.replace("Indoor ", "")).join(", ");
              return (
                <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.borderLight}`, overflow: "hidden", transition: "box-shadow 0.3s, transform 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 30px rgba(26,138,125,0.12)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <div style={{ position: "relative", height: 200, overflow: "hidden" }}>
                    <img src={JUNIOR_IMAGE} alt="Junior Sport" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)" }} />
                    <div style={{ position: "absolute", bottom: 16, left: 20, right: 20 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: "1.5rem" }}>🧒</span>
                        <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: C.white }}>Junior Sport</h3>
                      </div>
                    </div>
                    <div style={{ position: "absolute", top: 12, right: 12, background: "rgba(26,138,125,0.9)", padding: "4px 12px", borderRadius: 8, fontSize: "0.72rem", fontWeight: 600, color: C.white }}>Ages 4+</div>
                  </div>
                  <div style={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column" }}>
                    <p style={{ fontSize: "0.88rem", color: C.lightMuted, lineHeight: 1.6, marginBottom: 14, flex: 1 }}>
                      After-school and weekend programs for kids of all ages. {juniorSports.length > 0 ? `Junior leagues in ${juniorNames}.` : "Fun, fitness, and skill development."} A safe, fun way to build confidence through sport.
                    </p>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: C.lightDim }}>
                        <Calendar size={14} /> {juniorDays || "Fri, Sat, Sun"}
                      </div>
                      {/* 2026-05-27: opens an in-page sport picker instead of
                          dumping new parents on the WISA login screen. */}
                      <button onClick={() => setJuniorModalOpen(true)}
                        style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.82rem", fontWeight: 600, color: C.teal, background: "transparent", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
                        Browse Programs <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* ── JUNIOR SPORT / COURT HIRE / PARTIES (white bg) ── */}
      <section style={{ padding: "80px 0", background: C.white }}>
        <div className="wc-container">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.teal, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>More at Wildcatz</div>
            <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 900, color: C.lightText, letterSpacing: "-0.5px" }}>Something for Everyone</h2>
          </div>
          <div className="wc-more-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {[
              {
                title: "Junior Sport",
                emoji: "🧒",
                desc: "Ages 4 and up! Junior leagues in cricket, soccer, netball and volleyball. A safe, fun way for kids to learn new skills, make friends, and build confidence through sport.",
                priceFrom: "from $15.50/game",
                cta: "Browse Programs",
                // 2026-05-27: opens in-page sport picker (see JuniorSportsModal below)
                // instead of taking parents straight to the WISA login screen.
                onClick: () => setJuniorModalOpen(true),
                color: C.teal,
              },
              {
                title: "Court Hire",
                emoji: "🏟️",
                desc: "Book a court for your team training, casual games, or private sessions. Multiple indoor courts available with flexible time slots to suit your schedule.",
                priceFrom: "from $55/hr",
                cta: "Book Now",
                href: "https://www.wisasports.com/wildcatz/book",
                color: C.teal,
              },
              {
                title: "Birthday Parties",
                emoji: "🎉",
                desc: "The ultimate birthday party venue! Choose from sport-themed parties, Nerf wars, and more. We handle everything so you can enjoy the day. Packages for all ages.",
                // 2026-05-27: per-child price reads way better than the
                // package total. Sports party = $400 base for 12 kids = $33/kid.
                priceFrom: "from $33/child",
                cta: "Book a Party",
                href: "https://www.wisasports.com/wildcatz/book-party",
                color: C.red,
              },
              {
                title: "Ninja Parc",
                emoji: "🥷",
                desc: "All-ages obstacle course inspired by Ninja Warrior! Test your strength, agility, and balance across challenging courses. Perfect for kids, teens, and adults.",
                priceFrom: "from $22",
                cta: "Book Ninja Parc",
                href: "https://book.ninjaparc.com.au/location/townsville",
                color: "#7cc53e",
              },
              {
                title: "Little Ninjas Playground",
                emoji: "🧒",
                desc: "A dedicated play area for under 6's! Safe, fun, and designed for little adventurers. Let the kids burn off energy while you relax with a coffee.",
                priceFrom: "from $12",
                cta: "Book Now",
                href: "https://book.ninjaparc.com.au/location/townsville",
                color: "#7cc53e",
              },
            ].map((item, i) => (
              <div key={i} style={{ background: C.offWhite, borderRadius: 16, border: `1px solid ${C.borderLight}`, padding: "32px 24px", textAlign: "center", transition: "box-shadow 0.3s, transform 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 8px 24px ${item.color}14`; e.currentTarget.style.transform = "translateY(-3px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "translateY(0)"; }}>
                <div style={{ fontSize: "2.5rem", marginBottom: 16 }}>{item.emoji}</div>
                <h3 style={{ fontSize: "1.15rem", fontWeight: 800, color: C.lightText, marginBottom: 10 }}>{item.title}</h3>
                {/* 2026-05-27: optional "from $X" pill — only renders when the
                    item provides priceFrom. Sits above the description so
                    customers see the price anchor before reading the blurb. */}
                {item.priceFrom && (
                  <div style={{ display: "inline-block", fontSize: "0.7rem", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: item.color, background: `${item.color}14`, padding: "4px 10px", borderRadius: 4, marginBottom: 12 }}>
                    {item.priceFrom}
                  </div>
                )}
                <p style={{ fontSize: "0.88rem", color: C.lightMuted, lineHeight: 1.6, marginBottom: 20 }}>{item.desc}</p>
                {item.onClick ? (
                  <button onClick={item.onClick}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, background: item.color, color: C.white, padding: "10px 22px", borderRadius: 10, fontWeight: 700, fontSize: "0.85rem", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                    {item.cta} <ArrowRight size={14} />
                  </button>
                ) : (
                  <a href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, background: item.color, color: C.white, padding: "10px 22px", borderRadius: 10, fontWeight: 700, fontSize: "0.85rem" }}>
                    {item.cta} <ArrowRight size={14} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WISA QUICK LINKS (white bg, teal accents) ── */}
      <section style={{ padding: "60px 0", background: C.white, borderTop: `1px solid ${C.borderLight}`, borderBottom: `1px solid ${C.borderLight}` }}>
        <div className="wc-container">
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <h2 style={{ fontSize: "1.4rem", fontWeight: 900, color: C.lightText, marginBottom: 8 }}>Quick Links</h2>
            <p style={{ fontSize: "0.9rem", color: C.lightMuted }}>Everything you need — powered by WISA</p>
          </div>
          <div className="wc-quicklinks-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            {[
              { label: "Game Times", desc: "View upcoming fixtures", href: "https://www.wisasports.com/wildcatz/fixtures", icon: Calendar, color: C.teal },
              { label: "Ladders", desc: "Competition standings", href: "https://www.wisasports.com/wildcatz/ladder", icon: BarChart3, color: C.teal },
              { label: "Live Scores", desc: "Real-time match scores", href: "https://www.wisasports.com/wildcatz/scores", icon: Activity, color: C.red },
              { label: "Register", desc: "Create your account", href: "https://www.wisasports.com/wildcatz/register", icon: Users, color: C.teal },
            ].map((link, i) => (              <a key={i} href={link.href} target="_blank" rel="noopener noreferrer"
                style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "24px 16px", borderRadius: 14, border: `1px solid ${C.borderLight}`, transition: "all 0.2s", background: C.white }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.teal; e.currentTarget.style.boxShadow = "0 4px 16px rgba(26,138,125,0.1)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.borderLight; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "translateY(0)"; }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: link.color + "12", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                  <link.icon size={22} color={link.color} />
                </div>
                <div style={{ fontWeight: 700, fontSize: "0.92rem", color: C.lightText, marginBottom: 4 }}>{link.label}</div>
                <div style={{ fontSize: "0.78rem", color: C.lightMuted }}>{link.desc}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── ALL4INDOOR LEADERBOARD (dark bg) ── */}
      <section id="leaderboard" style={{ padding: "80px 0", background: C.black }}>
        <div className="wc-container">
          <div className="wc-leaderboard-layout" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
            {/* Left — info */}
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(108,92,231,0.1)", border: "1px solid rgba(108,92,231,0.2)", borderRadius: 20, padding: "6px 14px", marginBottom: 20 }}>
                <Globe size={12} color={C.purple} />
                <span style={{ fontSize: "0.75rem", fontWeight: 600, color: C.purple }}>Powered by All4Indoor</span>
              </div>
              <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 900, color: C.white, letterSpacing: "-0.5px", marginBottom: 16 }}>
                Your Stats. Tracked Globally.
              </h2>
              <p style={{ fontSize: "0.95rem", color: C.textMuted, lineHeight: 1.7, marginBottom: 28 }}>
                Every game at Wildcatz counts towards the national All4Indoor leaderboard. Track your rating, earn badges, and see how you stack up against players across Australia.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  { icon: BarChart3, text: "Real-time player ratings across all sports" },
                  { icon: Trophy, text: "Compete for sponsored prizes" },
                  { icon: TrendingUp, text: "Track your season-on-season improvement" },
                  { icon: Award, text: "Earn badges and climb the national rankings" },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(26,138,125,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <item.icon size={16} color={C.teal} />
                    </div>
                    <span style={{ fontSize: "0.88rem", color: C.text }}>{item.text}</span>
                  </div>
                ))}
              </div>
              <a href="https://www.all4indoor.com" target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 28, padding: "12px 24px", borderRadius: 10, background: "rgba(108,92,231,0.15)", border: "1px solid rgba(108,92,231,0.3)", color: C.purple, fontWeight: 600, fontSize: "0.88rem" }}>
                View Full Leaderboard <ExternalLink size={14} />
              </a>
            </div>

            {/* Right — leaderboard widget */}
            <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, overflow: "hidden" }}>
              <div style={{ padding: "18px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: "0.9rem", fontWeight: 800, color: C.white }}>Wildcatz Top Players</div>
                  <div style={{ fontSize: "0.72rem", color: C.textMuted, marginTop: 2 }}>All4Indoor National Rankings</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontWeight: 900, color: C.white, fontSize: "0.7rem", letterSpacing: "-0.3px", textTransform: "uppercase", display: "flex", alignItems: "center" }}>
                    ALL<span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: 3, background: C.teal, color: C.white, fontSize: "0.5rem", fontWeight: 900, margin: "0 1px" }}>4</span>INDOOR
                  </span>
                </div>
              </div>
              {topPlayers.map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 24px", borderBottom: i < topPlayers.length - 1 ? `1px solid ${C.border}` : "none", transition: "background 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background = C.cardHover}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ width: 28, fontSize: "0.82rem", fontWeight: 800, color: i < 3 ? C.teal : C.textDim }}>
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${p.rank}`}
                  </div>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: `linear-gradient(135deg, ${C.teal}44, ${C.purple}44)`, display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontSize: "0.7rem", fontWeight: 700 }}>
                    {p.name.split(" ").map(n => n[0]).join("")}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: "0.88rem", color: C.white }}>{p.name} {p.badge}</div>
                    <div style={{ fontSize: "0.72rem", color: C.textMuted }}>{p.sport}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 800, fontSize: "0.9rem", color: C.white }}>{p.rating}</div>
                    <div style={{ fontSize: "0.68rem", color: C.teal, fontWeight: 600 }}>
                      {p.isLive ? p.trend : `↑ ${p.trend}`}
                    </div>
                  </div>
                </div>
              ))}
              <a href="https://www.all4indoor.com" target="_blank" rel="noopener noreferrer"
                style={{ display: "block", padding: "14px", textAlign: "center", fontSize: "0.82rem", fontWeight: 600, color: C.purple, borderTop: `1px solid ${C.border}` }}>
                See All Rankings on All4Indoor →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS (light bg) ── */}
      <section style={{ padding: "80px 0", background: C.offWhite }}>
        <div className="wc-container">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.teal, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>What Players Say</div>
            <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 900, color: C.lightText, letterSpacing: "-0.5px" }}>Loved by Townsville</h2>
          </div>
          <div className="wc-testimonials-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{ background: C.white, borderRadius: 14, padding: "24px", border: `1px solid ${i === activeTestimonial ? C.teal : C.borderLight}`, transition: "border-color 0.5s", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 14 }}>
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} size={14} fill="#fbbf24" stroke="#fbbf24" />)}
                </div>
                <p style={{ fontSize: "0.88rem", color: C.lightText, lineHeight: 1.6, marginBottom: 16 }}>"{t.text}"</p>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: `linear-gradient(135deg, ${C.teal}, ${C.tealDark})`, display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontWeight: 800, fontSize: "0.75rem" }}>{t.name[0]}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.85rem", color: C.lightText }}>{t.name}</div>
                    <div style={{ fontSize: "0.72rem", color: C.lightMuted }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Halloween Spooktacular ───────────────────────────────────────
          2026-10-22 (Jake): "so trading cards still here remove". The trading
          cards section is gone. The range is vending-machine and counter only,
          and this page was the last place still advertising it as a thing you
          could come to the website for.

          What stays is the Halloween block that had been sitting inside it.
          That is not a trading-cards thing — it is a real event nine days out
          with 13 of 100 places gone — so it keeps its place as a section of
          its own rather than going down with the cards.

          The same one-shot timer clears this section AND its nav entry the
          moment the doors open, so nothing here outlives the event. */}
      {!halloweenPassed && (
      <section id="halloween" style={{ padding: "80px 0", background: C.darkBg, borderTop: `1px solid ${C.border}` }}>
        <div className="wc-container">
          <div style={{ textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(196,30,30,0.1)", border: "1px solid rgba(196,30,30,0.25)", borderRadius: 20, padding: "6px 16px", marginBottom: 16 }}>
              <Sparkles size={14} color={C.red} />
              <span style={{ fontSize: "0.78rem", fontWeight: 600, color: C.redLight }}>One afternoon only</span>
            </div>
            <h2 style={{ fontSize: "clamp(1.6rem, 3.4vw, 2.4rem)", fontWeight: 900, color: C.white, letterSpacing: "-0.5px", marginBottom: 8 }}>
              Halloween Spooktacular
            </h2>
            <p style={{ fontSize: "1rem", color: C.white, fontWeight: 700, marginTop: 0, marginBottom: 28 }}>
              Saturday 31 October · 1&ndash;3pm
            </p>
          </div>

          <CountdownTimer
            target={HALLOWEEN_EVENT}
            note={(label) => (
              <>🎃 Doors open <strong style={{ color: "#fff" }}>{label}</strong>. Free for members, $45 for everyone else.</>
            )}
          />

          <div style={{ textAlign: "center", marginTop: 28 }}>
            <a href={HALLOWEEN_RSVP_URL} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 10, background: C.red, color: C.white, padding: "16px 38px", borderRadius: 10, fontWeight: 800, fontSize: "1rem", textDecoration: "none" }}>
              RSVP — free for members <ArrowRight size={18} />
            </a>
            <p style={{ fontSize: "0.8rem", color: C.textMuted, marginTop: 12 }}>
              RSVPs close Wednesday 28 October.
            </p>
          </div>
        </div>
      </section>
      )}


      {/* ── COMING SOON TEASER (dark bg) ── */}
      {/* ─── "MORE THAN JUST SPORT" SECTION ──────────────────────────
          Before the venue's reveal date → big animated countdown timer
          to build hype (Days / Hours / Mins / Secs). Heading + description
          + app store badges still visible.
          After the reveal date → swaps automatically to the 6-activity
          grid + "See Full Details" link. No code change needed. */}
      {!EXPANSION_ON_HOLD && (() => {
        // 2026-10-22 (Jake): "dont want to reveal yet dont do a countdown just
        // say more to be announced soon".
        //
        // EXPANSION_REVEAL_READY holds the teaser shut on its own, instead of
        // the site reading venues.membership_reveal_at for the answer. That
        // date is currently set to 1 November and lib/revealDate.js is explicit
        // that it is ONE answer shared by four surfaces — the member pages, the
        // waiting list, the activity booking API and this site. Moving it to
        // keep the website quiet would have reached into three other things
        // nobody asked for. The website holds its own line; the date is still
        // read, so when the reveal is wanted this flag is the only edit.
        //
        // No date still means not revealed: reading a missing date as "the date
        // has passed" would publish the whole expansion the first time the API
        // hiccupped.
        const beforeReveal = !EXPANSION_REVEAL_READY
          || !comingSoonRevealDate || new Date() < comingSoonRevealDate;
        return (
      <section id="coming-soon-preview" style={{ padding: "80px 0", background: C.black }}>
        <div className="wc-container">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(196,30,30,0.1)", border: "1px solid rgba(196,30,30,0.25)", borderRadius: 20, padding: "6px 16px", marginBottom: 16 }}>
              <Sparkles size={14} color={C.red} />
              <span style={{ fontSize: "0.78rem", fontWeight: 600, color: C.redLight }}>
                {beforeReveal ? "You've Seen The Arcade" : "Expanding Late 2026"}
              </span>
            </div>
            <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 900, color: C.white, letterSpacing: "-0.5px", marginBottom: 12 }}>
              {beforeReveal ? "That Was Just The Start" : "More Than Just Sport"}
            </h2>
            <p style={{ fontSize: "0.95rem", color: C.textMuted, maxWidth: 560, margin: "0 auto 20px" }}>
              {beforeReveal
                ? "You've watched the arcade go in. That's one of five. We're not saying a word about the rest yet — get the app and you'll be the first to find out what's coming."
                : "Wildcatz is growing. Five exciting new activities are on their way. Download our app to stay up to date with all the news!"}
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" style={{ height: 40 }} />
              </a>
              <GooglePlayBadge height={40} />
            </div>
          </div>

          {beforeReveal ? (
            <div style={{ textAlign: "center", padding: "20px 0 6px" }}>
              <p style={{ fontSize: "clamp(1.15rem, 2.6vw, 1.6rem)", fontWeight: 800, color: C.white, letterSpacing: "-0.3px", marginBottom: 10 }}>
                More to be announced soon
              </p>
              <p style={{ fontSize: "0.9rem", color: C.textMuted, maxWidth: 440, margin: "0 auto" }}>
                No date yet. Get the app and you'll hear it from us first.
              </p>
            </div>
          ) : (
            <>
              <div className="wc-coming-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                {COMING_ACTIVITIES.map((item, i) => (
                  <div key={i} style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: "20px 16px", textAlign: "center", transition: "border-color 0.3s, transform 0.2s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = item.color + "55"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = "translateY(0)"; }}>
                    <div style={{ fontSize: "1.8rem", marginBottom: 8 }}>{item.emoji}</div>
                    <div style={{ fontWeight: 800, fontSize: "0.88rem", color: C.white, marginBottom: 4 }}>{item.name}</div>
                    <p style={{ fontSize: "0.75rem", color: C.textMuted, lineHeight: 1.4, marginBottom: 10 }}>{item.desc}</p>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.68rem", fontWeight: 700, color: item.color, background: item.color + "15", border: `1px solid ${item.color}33`, borderRadius: 6, padding: "4px 10px" }}>
                      <Timer size={10} /> Coming Soon
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ textAlign: "center", marginTop: 36 }}>
                <a href="#/coming-soon"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(196,30,30,0.12)", border: "1px solid rgba(196,30,30,0.3)", color: C.redLight, padding: "12px 28px", borderRadius: 10, fontWeight: 700, fontSize: "0.9rem" }}>
                  See Full Details <ArrowRight size={16} />
                </a>
              </div>
            </>
          )}
        </div>
      </section>
        );
      })()}

      {/* ── BOOK NOW CTA (teal bg) ── */}
      <section id="book" style={{ padding: "80px 0", background: C.teal, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -100, right: -100, width: 300, height: 300, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
        <div style={{ position: "absolute", bottom: -80, left: -80, width: 250, height: 250, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
        <div className="wc-container" style={{ position: "relative", textAlign: "center" }}>
          <h2 style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 900, color: C.white, letterSpacing: "-1px", marginBottom: 16 }}>Ready to Play?</h2>
          <p style={{ fontSize: "1.05rem", color: "rgba(255,255,255,0.85)", maxWidth: 500, margin: "0 auto 36px", lineHeight: 1.7 }}>
            Book a court, organise a birthday party, sign up the kids for junior sport, or register your team for a league.
          </p>
          <div className="wc-book-buttons" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginBottom: 16 }}>
            <a href="https://www.wisasports.com/wildcatz/book" target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 10, background: C.white, color: C.teal, padding: "22px 56px", borderRadius: 14, fontWeight: 800, fontSize: "1.35rem", boxShadow: "0 6px 28px rgba(0,0,0,0.2)", letterSpacing: "-0.3px", transition: "transform 0.15s, box-shadow 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 36px rgba(0,0,0,0.25)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 6px 28px rgba(0,0,0,0.2)"; }}>
              Book Now →
            </a>
          </div>
          <div style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.6)", marginTop: 12, marginBottom: 24 }}>
            All bookings and registrations online — quick and easy.
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" style={{ height: 44 }} />
            </a>
            <GooglePlayBadge height={44} />
          </div>
          <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)", marginTop: 10 }}>
            Download the app for push notifications, live scores & news
          </div>
        </div>
      </section>

      {/* ── WISA + ALL4INDOOR BADGES (light bg) ── */}
      <section style={{ padding: "60px 0", borderTop: `1px solid ${C.borderLight}`, borderBottom: `1px solid ${C.borderLight}`, background: C.offWhite }}>
        <div className="wc-container">
          <div className="wc-badges-row" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 48, flexWrap: "wrap" }}>
            <a href="https://www.wisasports.com/wildcatz/register" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 28px", borderRadius: 14, background: C.white, border: `1px solid ${C.borderLight}`, transition: "border-color 0.3s, box-shadow 0.3s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.teal; e.currentTarget.style.boxShadow = "0 4px 16px rgba(26,138,125,0.1)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.borderLight; e.currentTarget.style.boxShadow = "none"; }}>
              <Shield size={24} color={C.teal} />
              <div>
                <div style={{ fontWeight: 800, fontSize: "0.9rem", color: C.lightText }}>WISA Verified Venue</div>
                <div style={{ fontSize: "0.75rem", color: C.lightMuted }}>Official venue management & bookings</div>
              </div>
            </a>
            <a href="https://www.all4indoor.com" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 28px", borderRadius: 14, background: C.white, border: `1px solid ${C.borderLight}`, transition: "border-color 0.3s, box-shadow 0.3s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.purple; e.currentTarget.style.boxShadow = "0 4px 16px rgba(108,92,231,0.1)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.borderLight; e.currentTarget.style.boxShadow = "none"; }}>
              <span style={{ fontWeight: 900, fontSize: "0.85rem", letterSpacing: "-0.3px", textTransform: "uppercase", display: "flex", alignItems: "center" }}>
                <span style={{ color: C.lightText }}>ALL</span>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: 5, background: C.teal, color: C.white, fontSize: "0.65rem", fontWeight: 900, margin: "0 2px" }}>4</span>
                <span style={{ color: C.lightText }}>INDOOR</span>
              </span>
              <div>
                <div style={{ fontWeight: 800, fontSize: "0.9rem", color: C.lightText }}>Stats & Rankings</div>
                <div style={{ fontSize: "0.75rem", color: C.lightMuted }}>Global player tracking & tournaments</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ── CONTACT / ABOUT (dark bg) ── */}
      <section id="contact" style={{ padding: "80px 0", background: C.black }}>
        <div className="wc-container">
          <div id="about" className="wc-contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
            <div>
              <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.teal, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>Get in Touch</div>
              <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 900, color: C.white, letterSpacing: "-0.5px", marginBottom: 20 }}>Come Play With Us</h2>
              <p style={{ fontSize: "0.95rem", color: C.textMuted, lineHeight: 1.7, marginBottom: 32 }}>
                Whether you're part of a team or flying solo, we've got a place for you. Corporate events, sports parties, Nerf wars, school holiday programs — we do it all.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {[
                  { icon: Phone, label: "(07) 4723 1414", href: "tel:0747231414" },
                  { icon: Mail, label: "Info@wildcatzindoorsports.com.au", href: "mailto:Info@wildcatzindoorsports.com.au" },
                  { icon: MapPin, label: "Wildcatz Indoor Sports, Townsville QLD", href: null },
                  { icon: Clock, label: "Open 7 days — see fixtures for game times", href: null },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(26,138,125,0.12)", border: "1px solid rgba(26,138,125,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <item.icon size={18} color={C.teal} />
                    </div>
                    {item.href ? (
                      <a href={item.href} style={{ fontSize: "0.9rem", color: C.text, fontWeight: 500 }}>{item.label}</a>
                    ) : (
                      <span style={{ fontSize: "0.9rem", color: C.text, fontWeight: 500 }}>{item.label}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, padding: "32px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <h3 style={{ fontWeight: 800, fontSize: "1.2rem", color: C.white, marginBottom: 8 }}>Quick Links</h3>
              <p style={{ fontSize: "0.85rem", color: C.textMuted, marginBottom: 24 }}>Everything you need is on our WISA platform:</p>
              {[
                { label: "Register / Sign Up", desc: "Create your WISA account", href: "https://www.wisasports.com/wildcatz/register" },
                { label: "Game Times & Fixtures", desc: "See all upcoming games", href: "https://www.wisasports.com/wildcatz/fixtures" },
                { label: "Ladders & Standings", desc: "View competition ladders", href: "https://www.wisasports.com/wildcatz/ladder" },
                { label: "Live Scores", desc: "Real-time match scores", href: "https://www.wisasports.com/wildcatz/scores" },
                { label: "Player Stats", desc: "Your All4Indoor profile", href: "https://www.all4indoor.com" },
              ].map((link, i) => (
                <a key={i} href={link.href} target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderRadius: 10, marginBottom: 8, background: "transparent", border: `1px solid ${C.border}`, transition: "all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = C.cardHover; e.currentTarget.style.borderColor = C.teal + "33"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = C.border; }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.88rem", color: C.white }}>{link.label}</div>
                    <div style={{ fontSize: "0.75rem", color: C.textMuted }}>{link.desc}</div>
                  </div>
                  <ChevronRight size={16} color={C.textMuted} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ (offWhite bg) ──
          2026-05-27: answers the 8 questions parents/customers ask before
          they hit Book or Register. Accordion so it stays compact. Pure
          local copy — no external dependencies. Add/remove items by
          editing the FAQS array below. */}
      <section id="faq" style={{ padding: "72px 0", background: C.offWhite, borderTop: `1px solid ${C.borderLight}` }}>
        <div className="wc-container" style={{ maxWidth: 820 }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.teal, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>Common Questions</div>
            <h2 style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 900, color: C.lightText, letterSpacing: "-0.5px" }}>FAQ</h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { q: "Do I need a team to play, or can I join one?", a: "Both work. Bring a full team and we'll slot you into a comp; rocking up solo or with a mate works too — tap 'Register a Team' and tick 'Looking for players' and we'll match you with a side that needs bodies." },
              { q: "What should I wear?", a: "Flat-soled, closed-in court shoes are compulsory — no studs, no Crocs, no bare feet. Comfortable sports clothing. Bring a water bottle. We supply balls and equipment for league play; bring your own for court hire." },
              { q: "Can spectators come watch? Is there a fee?", a: "Yes, spectators are always free. Bring the kids, partners, parents — the more the better. There's seating around every court and a cafe area for the bigger games." },
              { q: "What if I can't make a game?", a: "Tap 'Can't Make It' on the fixture in the WISA app at least 48 hours before the game and your token is refunded. Inside 48 hours it's locked in (so your team can plan around it)." },
              { q: "How does pricing work?", a: "Adult league games run as little as $12–$17 a game when you grab a membership (memberships from $30/wk for 3 games), or $15–$22 a game with no lock-in. Junior sport from $15.50 per game. Birthday parties from $33/child. Court hire from $55/hr off-peak ($99/hr peak), beach courts $88/hr — see the Book a Court page for the full breakdown." },
              { q: "Is there parking?", a: "Yes — free on-site parking for everyone. Plenty of spots even on busy nights, with extra overflow parking along the street." },
              { q: "Do you do school holiday programs?", a: "Yes — multi-sport School Holiday Programs run every Easter, July, September and Christmas holidays. Full-day and half-day options, capped at 24 spots per day. Bookings open about 4 weeks before each block." },
              { q: "Can I book a court for a private group?", a: "Absolutely. Hit 'Book a Court' on the homepage — pick your court, your time, pay online and you're in. Need a longer block (corporate, school, church group)? Email us at info@wildcatzindoorsports.com.au for custom rates." },
            ].map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} style={{ background: C.white, border: `1px solid ${isOpen ? C.teal : C.borderLight}`, borderRadius: 12, overflow: "hidden", transition: "border-color 0.15s" }}>
                  <button onClick={() => setOpenFaq(isOpen ? null : i)}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "18px 22px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                    <span style={{ fontSize: "1rem", fontWeight: 700, color: C.lightText, lineHeight: 1.35 }}>{item.q}</span>
                    <ChevronRight size={18} color={isOpen ? C.teal : C.lightMuted}
                      style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0)", transition: "transform 0.2s", flexShrink: 0 }} />
                  </button>
                  {isOpen && (
                    <div style={{ padding: "0 22px 20px", fontSize: "0.92rem", color: C.lightMuted, lineHeight: 1.65 }}>
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: "center", marginTop: 28, fontSize: "0.88rem", color: C.lightMuted }}>
            Still got a question? <a href="mailto:info@wildcatzindoorsports.com.au" style={{ color: C.teal, fontWeight: 700 }}>Email us</a> or call <a href="tel:0747231414" style={{ color: C.teal, fontWeight: 700 }}>(07) 4723 1414</a>.
          </div>
        </div>
      </section>

      {/* ── CONTACT / VISIT US (white bg) ──
          2026-05-27: critical local-business info that was missing entirely
          from the marketing site — phone, email, opening hours, map and
          socials. Without this, parents had no way to contact the venue
          short of calling around or searching elsewhere. */}
      <section id="contact" style={{ padding: "72px 0", background: C.white, borderTop: `1px solid ${C.borderLight}` }}>
        <div className="wc-container">
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.teal, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>Visit / Contact</div>
            <h2 style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 900, color: C.lightText, letterSpacing: "-0.5px" }}>Drop in or Get in Touch</h2>
          </div>
          <div className="wc-contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, alignItems: "stretch" }}>
            {/* Left — contact details */}
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {/* Phone */}
              <a href="tel:0747231414" style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", background: C.offWhite, borderRadius: 12, border: `1px solid ${C.borderLight}`, textDecoration: "none", transition: "border-color 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.teal}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.borderLight}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: `${C.teal}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Phone size={18} color={C.teal} />
                </div>
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, color: C.lightMuted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>Phone</div>
                  <div style={{ fontSize: "1rem", fontWeight: 700, color: C.lightText }}>(07) 4723 1414</div>
                </div>
              </a>
              {/* Email */}
              <a href="mailto:info@wildcatzindoorsports.com.au" style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", background: C.offWhite, borderRadius: 12, border: `1px solid ${C.borderLight}`, textDecoration: "none", transition: "border-color 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.teal}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.borderLight}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: `${C.teal}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Mail size={18} color={C.teal} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, color: C.lightMuted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>Email</div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 700, color: C.lightText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>info@wildcatzindoorsports.com.au</div>
                </div>
              </a>
              {/* Address */}
              <a href="https://www.google.com/maps/search/?api=1&query=Wildcatz+Indoor+Sports+Townsville" target="_blank" rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", background: C.offWhite, borderRadius: 12, border: `1px solid ${C.borderLight}`, textDecoration: "none", transition: "border-color 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.teal}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.borderLight}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: `${C.teal}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <MapPin size={18} color={C.teal} />
                </div>
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, color: C.lightMuted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>Address</div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 700, color: C.lightText }}>Wildcatz Indoor Sports, Townsville</div>
                  <div style={{ fontSize: "0.78rem", color: C.teal, marginTop: 2 }}>Get directions →</div>
                </div>
              </a>
              {/* Opening hours — pulled from /Book.js operating-hours rules */}
              <div style={{ padding: "16px 18px", background: C.offWhite, borderRadius: 12, border: `1px solid ${C.borderLight}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: `${C.teal}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Clock size={18} color={C.teal} />
                  </div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, color: C.lightMuted, letterSpacing: 1, textTransform: "uppercase" }}>Opening Hours</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: "0.88rem", color: C.lightText }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>Monday – Friday</span><span style={{ fontWeight: 600 }}>10:00 am – 9:00 pm</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>Saturday</span><span style={{ fontWeight: 600 }}>8:00 am – 3:00 pm</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>Sunday</span><span style={{ fontWeight: 600 }}>9:00 am – 6:00 pm</span></div>
                </div>
              </div>
              {/* Social media — placeholders. Update hrefs below with your actual handles. */}
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                {[
                  { label: "Facebook", icon: "f", href: "https://www.facebook.com/wildcatzindoorsports" },
                  { label: "Instagram", icon: "📸", href: "https://www.instagram.com/wildcatzindoorsports" },
                ].map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                    style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 14px", background: C.lightText, color: C.white, borderRadius: 10, fontSize: "0.85rem", fontWeight: 700, textDecoration: "none", letterSpacing: 0.3 }}>
                    <span style={{ fontSize: "0.95rem" }}>{s.icon}</span> {s.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Right — Google Map embed (uses business name search, no API key needed) */}
            <div style={{ borderRadius: 14, overflow: "hidden", border: `1px solid ${C.borderLight}`, minHeight: 420 }}>
              <iframe
                title="Wildcatz Indoor Sports location"
                src="https://www.google.com/maps?q=Wildcatz+Indoor+Sports+Townsville&output=embed"
                width="100%" height="100%"
                style={{ border: 0, display: "block", minHeight: 420 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── FRANCHISE ENQUIRY (dark bg, subtle) ──
          2026-05-27: hidden until 1 December 2026 Brisbane time. Auto-shows
          after that date — no code change needed. */}
      {new Date() >= FRANCHISE_REVEAL_DATE && (
      <section style={{ padding: "48px 0", background: C.black, borderTop: `1px solid ${C.border}` }}>
        <div className="wc-container">
          <div className="wc-franchise-layout" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "center" }}>
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.25)", borderRadius: 20, padding: "5px 14px", marginBottom: 14 }}>
                <Globe size={12} color={C.orange} />
                <span style={{ fontSize: "0.72rem", fontWeight: 600, color: C.orange }}>Franchise Opportunity</span>
              </div>
              <h3 style={{ fontSize: "1.3rem", fontWeight: 800, color: C.white, marginBottom: 8 }}>Own a Wildcatz Venue</h3>
              <p style={{ fontSize: "0.88rem", color: C.textMuted, lineHeight: 1.6 }}>
                Interested in bringing Wildcatz Indoor Sports to your area? We're expanding and looking for franchise partners across Australia. Get in touch to learn more.
              </p>
            </div>
            <div style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: "24px" }}>
              {franchiseStatus === "sent" ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ fontSize: "2rem", marginBottom: 8 }}>✅</div>
                  <p style={{ fontWeight: 700, color: C.white, marginBottom: 4 }}>Enquiry Sent!</p>
                  <p style={{ fontSize: "0.82rem", color: C.textMuted }}>We'll be in touch soon.</p>
                </div>
              ) : (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setFranchiseStatus("sending");
                  try {
                    // The response was ignored, so "sent" was shown whatever
                    // came back — including the HTML the relative URL returned.
                    // An enquiry form that lies about delivering is worse than
                    // one that errors.
                    const res = await fetch(API_BASE + "/api/franchise-enquiry", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(franchiseForm),
                    });
                    if (!res.ok) throw new Error(`franchise-enquiry: ${res.status}`);
                    setFranchiseStatus("sent");
                  } catch { setFranchiseStatus("error"); }
                }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <input placeholder="Your name" required value={franchiseForm.name} onChange={e => setFranchiseForm(f => ({ ...f, name: e.target.value }))}
                      style={{ background: C.cardHover, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.white, fontSize: "0.85rem", outline: "none", fontFamily: "inherit" }} />
                    <input placeholder="Phone" required value={franchiseForm.phone} onChange={e => setFranchiseForm(f => ({ ...f, phone: e.target.value }))}
                      style={{ background: C.cardHover, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.white, fontSize: "0.85rem", outline: "none", fontFamily: "inherit" }} />
                  </div>
                  <input placeholder="Email address" type="email" required value={franchiseForm.email} onChange={e => setFranchiseForm(f => ({ ...f, email: e.target.value }))}
                    style={{ width: "100%", background: C.cardHover, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.white, fontSize: "0.85rem", outline: "none", marginBottom: 10, boxSizing: "border-box", fontFamily: "inherit" }} />
                  <textarea placeholder="Tell us about your interest..." rows={3} value={franchiseForm.message} onChange={e => setFranchiseForm(f => ({ ...f, message: e.target.value }))}
                    style={{ width: "100%", background: C.cardHover, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.white, fontSize: "0.85rem", outline: "none", resize: "vertical", marginBottom: 12, boxSizing: "border-box", fontFamily: "inherit" }} />
                  <button type="submit" disabled={franchiseStatus === "sending"}
                    style={{ width: "100%", background: C.orange, color: C.white, border: "none", borderRadius: 8, padding: "11px", fontSize: "0.88rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    {franchiseStatus === "sending" ? "Sending..." : franchiseStatus === "error" ? "Try Again" : "Send Enquiry"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── FOOTER (dark bg with teal bar) ── */}
      <footer style={{ borderTop: `3px solid ${C.teal}`, padding: "40px 0", background: C.darkBg }}>
        <div className="wc-container">
          <div className="wc-footer-grid" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img src="/wildcatz-logo.png" alt="Wildcatz" style={{ height: 32, opacity: 0.8 }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 24, fontSize: "0.78rem", color: C.textMuted, flexWrap: "wrap" }}>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" style={{ height: 32 }} />
              </a>
              <GooglePlayBadge height={32} />
              <span>Powered by <a href="https://www.wisasports.com" target="_blank" rel="noopener noreferrer" style={{ color: C.teal, fontWeight: 600 }}>WISA</a></span>
              <span>Stats by <a href="https://www.all4indoor.com" target="_blank" rel="noopener noreferrer" style={{ color: C.purple, fontWeight: 600 }}>All4Indoor</a></span>
            </div>
            <div style={{ fontSize: "0.75rem", color: C.textDim }}>
              © {new Date().getFullYear()} Wildcatz Indoor Sports. All rights reserved.
            </div>
          </div>
        </div>
      </footer>

      {/* ── STYLES ── */}
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

        * { box-sizing: border-box; }
        a { text-decoration: none; }

        .wc-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

        @media (max-width: 768px) {
          .wc-desktop-nav { display: none !important; }
          .wc-mobile-burger { display: block !important; }
          .wc-hero-live { display: none !important; }
          .wc-hero-mascot { display: none !important; }
          .wc-sports-grid { grid-template-columns: 1fr !important; }
          .wc-leaderboard-layout { grid-template-columns: 1fr !important; }
          .wc-testimonials-grid { grid-template-columns: 1fr !important; }
          .wc-contact-grid { grid-template-columns: 1fr !important; }
          .wc-franchise-layout { grid-template-columns: 1fr !important; }
          .wc-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .wc-coming-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .wc-quicklinks-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .wc-more-grid { grid-template-columns: 1fr !important; }
          .wc-book-buttons { flex-direction: column !important; }
          .wc-book-buttons a { justify-content: center; }
          .wc-badges-row { flex-direction: column !important; gap: 16px !important; }
          .wc-footer-grid { flex-direction: column !important; text-align: center !important; }
          .wc-nav-logo { height: 36px !important; }
        }
        @media (max-width: 480px) {
          .wc-steps-grid { grid-template-columns: 1fr !important; }
          .wc-coming-grid { grid-template-columns: 1fr !important; }
          .wc-quicklinks-grid { grid-template-columns: 1fr !important; }
          .wc-hero-title { font-size: 2rem !important; }
          .wc-hero-buttons { flex-direction: column !important; }
          .wc-hero-buttons a { text-align: center; justify-content: center; }
        }
        @media (min-width: 769px) {
          .wc-mobile-burger { display: none !important; }
          .wc-mobile-menu { display: none !important; }
        }
      `}</style>

      {/* ── JUNIOR SPORTS PICKER MODAL ──────────────────────────────────
          Opens when a parent clicks "Browse Programs" on either Junior
          Sport tile. Lists every junior-relevant sport at the venue with
          day, age range, brief blurb, and a one-tap "Register your child"
          CTA that lands on the WISA register page pre-filled for that
          sport. Way better than the old behaviour (dump them on /login).
          2026-05-27. */}
      {juniorModalOpen && (() => {
        // Pull junior-relevant rows from the live sports state. Cricket,
        // Soccer, Netball, Volleyball and Rounders all run junior programs.
        // Filter to sports tagged "All Ages" or "Juniors" so we only show
        // ones the venue actually offers to kids right now.
        const juniors = (sports || []).filter(s => s.ages === 'All Ages' || s.ages === 'Juniors');
        const SPORT_AGE = {
          cricket: 'Ages 5–14',
          soccer: 'Ages 4–14',
          netball: 'Ages 5–14',
          volleyball: 'Ages 8–14',
          rebound: 'Ages 8–14',
          rounders: 'Ages 6–14',
        };
        const SPORT_BLURB = {
          cricket: 'Indoor cricket with soft balls — perfect for new players. Learn to bat, bowl and field in a fun, safe setup.',
          soccer: 'Small-sided indoor soccer — high touches per kid, lots of running, lots of goals. All skill levels welcome.',
          netball: 'Modified netball for juniors. Learn passing, footwork and teamwork from coaches who love the game.',
          volleyball: 'Junior volleyball on the beach courts. Foundation skills + game play in every session.',
          rebound: 'Junior volleyball on the beach courts. Foundation skills + game play in every session.',
          rounders: 'A bat-and-ball blast — kids LOVE rounders. Easy to pick up, hard to stop playing.',
        };
        return (
          <div onClick={() => setJuniorModalOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: C.white, borderRadius: 20, maxWidth: 720, width: '100%', boxShadow: '0 20px 80px rgba(0,0,0,0.4)', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ background: `linear-gradient(135deg, ${C.teal} 0%, ${C.tealDark} 100%)`, padding: '28px 28px 24px', color: C.white, position: 'relative' }}>
                <button onClick={() => setJuniorModalOpen(false)}
                  style={{ position: 'absolute', top: 14, right: 14, background: 'rgba(255,255,255,0.18)', border: 'none', color: C.white, width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={16} />
                </button>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.9, marginBottom: 8 }}>Junior Sport · Ages 4+</div>
                <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 1.8rem)', fontWeight: 900, margin: '0 0 8px', lineHeight: 1.15 }}>Pick the sport for your child</h2>
                <p style={{ fontSize: '0.92rem', opacity: 0.92, margin: 0, lineHeight: 1.5 }}>
                  After-school and weekend programs. Coached. Safe. Heaps of fun. Tap a sport to register.
                </p>
              </div>

              {/* Sports list */}
              <div style={{ padding: '20px 24px' }}>
                {juniors.length === 0 ? (
                  <div style={{ padding: '32px 12px', textAlign: 'center', color: C.lightMuted, fontSize: '0.92rem' }}>
                    Loading junior programs…
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {juniors.map(sport => {
                      const slug = (sport.slug || sport.name || '').toLowerCase();
                      const img = sport.image;
                      const age = SPORT_AGE[slug] || 'Ages 4–14';
                      const blurb = SPORT_BLURB[slug] || sport.desc;
                      const cleanName = sport.name.replace(/^Indoor\s+/i, '');
                      // Pass sport slug so register page can pre-filter to junior comps of that sport
                      const registerUrl = `https://www.wisasports.com/wildcatz/register?junior=true&sport=${encodeURIComponent(slug)}`;
                      return (
                        <a key={sport.slug} href={registerUrl} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'stretch', gap: 14, background: C.offWhite, border: `1px solid ${C.borderLight}`, borderRadius: 12, overflow: 'hidden', textDecoration: 'none', transition: 'border-color 0.15s, transform 0.15s, box-shadow 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = C.teal; e.currentTarget.style.boxShadow = '0 4px 14px rgba(26,138,125,0.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = C.borderLight; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                          <img src={img} alt={cleanName}
                            style={{ width: 88, minHeight: 88, objectFit: 'cover', flexShrink: 0 }} />
                          <div style={{ flex: 1, padding: '12px 14px 12px 0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '1.05rem' }}>{sport.emoji}</span>
                              <h3 style={{ fontSize: '1rem', fontWeight: 800, color: C.lightText, margin: 0 }}>{cleanName}</h3>
                              <span style={{ fontSize: '0.65rem', fontWeight: 700, background: 'rgba(26,138,125,0.12)', color: C.teal, padding: '2px 8px', borderRadius: 4, letterSpacing: 0.5, textTransform: 'uppercase' }}>{age}</span>
                            </div>
                            <p style={{ fontSize: '0.82rem', color: C.lightMuted, lineHeight: 1.45, margin: '0 0 6px' }}>{blurb}</p>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', color: C.lightDim }}>
                                <Calendar size={12} /> {sport.days || 'Multiple days'}
                              </div>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', fontWeight: 700, color: C.teal }}>
                                Register <ArrowRight size={13} />
                              </span>
                            </div>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}

                {/* Help footer */}
                <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${C.borderLight}`, textAlign: 'center' }}>
                  <div style={{ fontSize: '0.85rem', color: C.lightMuted, marginBottom: 8 }}>
                    Not sure which sport is right? We're happy to help.
                  </div>
                  <a href="mailto:info@wildcatzindoorsports.com.au?subject=Junior%20Sport%20Enquiry"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', fontWeight: 700, color: C.teal, textDecoration: 'none', padding: '8px 16px', border: `1px solid ${C.teal}`, borderRadius: 10 }}>
                    <Mail size={14} /> Email us
                  </a>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
